import { startAuthentication } from '@simplewebauthn/browser';
import { collectDeviceIdentity } from '../core/devices/deviceIdentity';
import { resolveDevicePlatform } from '../adapters/device/platformDetector';
import { callFunction } from './firebase/FunctionsService';
import { storeDeviceSession } from './devicePasskeys';
import { resolveAuthEmail } from './FirebaseAuthService';

/**
 * Passwordless WebAuthn sign-in for devices that already registered a platform passkey.
 * Returns a Firebase custom token for `signInWithCustomToken`.
 */
export async function signInWithPasskey(username: string): Promise<{ customToken: string; email: string }> {
  const email = await resolveAuthEmail(username);
  const begin = await callFunction('beginPasskeySignIn', { email }) as {
    options?: Parameters<typeof startAuthentication>[0]['optionsJSON'];
    challengeId?: string;
  };
  if (!begin.options || !begin.challengeId) {
    throw Object.assign(new Error('Passkey sign-in unavailable.'), { code: 'auth/passkey-unavailable' });
  }

  const assertion = await startAuthentication({ optionsJSON: begin.options });
  const platform = await resolveDevicePlatform();
  const identity = collectDeviceIdentity(platform);
  const result = await callFunction('finishPasskeySignIn', {
    challengeId: begin.challengeId,
    response: assertion,
    label: identity.label,
    platform: identity.platform,
    userAgent: identity.userAgent,
  }) as {
    customToken?: string;
    deviceSessionToken?: string;
    deviceId?: string;
    email?: string;
  };

  if (!result.customToken) {
    throw Object.assign(new Error('Passkey sign-in failed.'), { code: 'auth/passkey-failed' });
  }

  storeDeviceSession(result.deviceSessionToken, result.deviceId);
  return { customToken: result.customToken, email: result.email || email };
}
